import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {CareproviderUnitService} from '../../../../../../services/careprovider-unit.service';
import {UrlBuilderService} from '../../../../../../services/url-builder.service';
import {TitleBuilderService} from '../../../../../../services/title-builder.service';
import {ToolsService} from '../../../../../../services/tools.service';
import {ModalsControlService} from '../../../../../../services/modals-control.service';
import {SeoMetaService} from '../../../../../../services/seo-meta.service';
import {LinkService} from '../../../../../../services/link.service';
import {PageViewsGa4ResolverService} from '../../../../../../services/page-views-ga4-resolver.service';
import {AdsStateService} from '../../../../../../services/ads-state.service';
import {GtmService} from '../../../../../../services/gtm.service';
import {CareproviderUnitUrlResolverService} from '../../../../service/careprovider-unit-url-resolver.service';
import {GtmGa4EventPushService} from '../../../../../../services/gtm-ga4-event-push.service';
import {BookingHelperService} from '../../../../../../services/booking-helper.service';
import {AppConfigService} from '../../../../../../services/app-config.service';
import {CareproviderUnitSchemaService} from '../../../../service/careprovider-unit-schema.service';
import {SearchReloadService} from '../../../../../../services/search/search-reload.service';
import {MetaTagFieldsEnum, MetaTypesEnum} from '../../../../../../enums/SeoEnums';
import {Subscription} from 'rxjs';
import {DfpStateData} from '../../../../../../types/DfpStateData';
import {CustomerFeatureTypeEnum, IsCustomerDfpEnum} from '../../../../../../enums/AdEnums';
import {RoutesAddonsEnums, RoutesEnums, RoutesQueryParamsEnum} from '../../../../../../enums/RoutesEnums';
import {MetaTag} from '../../../../../../types/MetaTag';
import {DLConversionTrackingEnum, DLPageTypeEnum, DLSegmentEnum} from '../../../../../../enums/DataLayerEnums';
import {DataLayerParams} from '../../../../../../types/DataLayerParams';
import {DataLayerParamsPageViewGA4} from '../../../../../../types/DataLayerParamsPageViewGA4';
import {BookingTypeEnum, OpenHourFieldNameTypesEnum} from '../../../../../../enums/BookingEnums';
import {ElementPositionEnum} from '../../../../../../enums/CssClassEnums';
import {WindowSizeService} from '../../../../../../services/window-size.service';
import {ModalCommand} from '../../../../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../../../../enums/ModalEnums';
import {CpruTabsService} from '../../../../../../services/cpru-tabs.service';
import {CpruTab} from '../../../../../../types/CpruTab';
import {GtmEventEnum} from '../../../../../../enums/GoogleEnums';
import {EventActionEnum, EventCategoryEnum} from '../../../../../../enums/GaTrackingEnums';
import {DataLayerParamsClickGA4} from '../../../../../../types/DataLayerParamsClickGA4';
import {LayoutService} from '../../../../../layout/layout.service';
import {DOCUMENT} from '@angular/common';

@Component({
  selector: 'app-cpru-main',
  templateUrl: './cpru-main.component.html',
  styleUrl: './cpru-main.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpruMainComponent implements OnInit, OnDestroy {
  public cpruTabsService = inject(CpruTabsService);
  public tabs = this.cpruTabsService.tabs;
  public highlightedSection = this.cpruTabsService.highlightedSection;
  protected pageTitle = signal<string>(null);
  protected showAddQuestionBtn = signal<boolean>(false);
  protected topSectionCssClass = signal<string>(ElementPositionEnum.NORMAL);
  protected bottomSectionCssClass = signal<string>(ElementPositionEnum.NORMAL);
  protected readonly dLPageTypeEnum = DLPageTypeEnum;
  protected readonly routesAddonsEnums = RoutesAddonsEnums;
  protected placeholderStyle = signal<{[klass: string]: any} | null | undefined>({display: 'none'});
  protected closeByHavePuffs = signal<boolean>(true);
  protected careProviderUnitUrl = computed<string[]>(() =>
    this.urlBuilder.CreateCareproviderUnitRouterLink(this.careproviderUnit()?.id, this.careproviderUnit()?.urlPath),
  );
  protected hasStaff = computed<boolean>(
    () => this.careproviderUnit()?.staffMembers && this.careproviderUnit().staffMembers.length > 0,
  );
  protected hasContact = computed<boolean>(
    () =>
      (this.careproviderUnit()?.caregiverContactInfo?.phoneNumbers &&
        this.careproviderUnit()?.caregiverContactInfo?.phoneNumbers.length > 0) ||
      (this.careproviderUnit()?.caregiverContactInfo?.websiteUrl !== null &&
        this.careproviderUnit()?.caregiverContactInfo?.websiteUrl !== ''),
  );
  protected hasOpenHours = computed<boolean>(
    () =>
      !this.tools.isNil(this.careproviderUnit()?.serviceHours) &&
      ((!this.tools.isNil(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.SURGERY]) &&
        Object.keys(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.SURGERY]).length > 0 &&
        this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.SURGERY].filter(
          (hour) => hour.fromDay > 0 && hour.toDay > 0,
        ).length > 0) ||
        (!this.tools.isNil(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.TELEPHONE]) &&
          Object.keys(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.TELEPHONE]).length > 0 &&
          this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.TELEPHONE].filter(
            (hour) => hour.fromDay > 0 && hour.toDay > 0,
          ).length > 0) ||
        (!this.tools.isNil(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.DROPIN]) &&
          Object.keys(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.DROPIN]).length > 0 &&
          this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.DROPIN].filter(
            (hour) => hour.fromDay > 0 && hour.toDay > 0,
          ).length > 0) ||
        (!this.tools.isNil(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.VISITINGHOURS]) &&
          Object.keys(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.VISITINGHOURS]).length > 0 &&
          this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.VISITINGHOURS].filter(
            (hour) => hour.fromDay > 0 && hour.toDay > 0,
          ).length > 0)),
  );
  protected hasReferral = computed<boolean>(
    () => this.careproviderUnit()?.referral_info && this.careproviderUnit()?.referral_info !== null,
  );
  protected shouldDisplayCloseBy = computed<boolean>(
    () =>
      !!(
        !this.careproviderUnit()?.isCustomer &&
        this.careproviderUnit()?.mainCaregiverUnitType &&
        this.careproviderUnit()?.mainCaregiverUnitType.urlPath &&
        this.careproviderUnit()?.address.latitude &&
        this.careproviderUnit()?.address.longitude
      ),
  );
  private trackedCareproviderUnitId = signal<number>(null);
  private careproviderUnitService = inject(CareproviderUnitService);
  protected careproviderUnit = this.careproviderUnitService.careproviderUnit;
  protected hasMap = computed<boolean>(
    () =>
      !this.tools.isNil(this.careproviderUnit()) &&
      !this.tools.isNil(this.careproviderUnit().address) &&
      this.careproviderUnit().address.latitude !== 0,
  );
  protected visitorQuestions = this.careproviderUnitService.visitorQuestions;
  protected survey = this.careproviderUnitService.survey;
  protected breadcrumbs = this.careproviderUnitService.breadcrumbs;
  protected bookingConfigurationData = this.careproviderUnitService.bookingConfigurationData;
  protected displayBookingBtn = this.careproviderUnitService.displayBookingBtn;
  protected isPreview = this.careproviderUnitService.isPreview;
  protected queryParams = this.careproviderUnitService.queryParams;
  private featureLabel = this.careproviderUnitService.featureLabel;
  private careproviderUnitAddOn = this.careproviderUnitService.careproviderUnitAddOn;
  private readonly listadigSuccessQueryParamName = 'listadigsuccess';
  private subscriptions: Subscription[] = [];
  private addReviewModalActionStarted = signal<boolean>(false);
  private windowSize = inject(WindowSizeService);
  private size = this.windowSize.windowSize;
  private readonly mobileTabsThreshold = 992;
  protected isMobile = computed<boolean>(() => this.size()[0] < this.mobileTabsThreshold);
  private readonly greyBgThreshold = 768;
  private closeByDataLoaded = signal<boolean>(false);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private urlBuilder: UrlBuilderService,
    private titleBuilder: TitleBuilderService,
    private tools: ToolsService,
    private modalControl: ModalsControlService,
    private metaService: SeoMetaService,
    private linkService: LinkService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private adsStateService: AdsStateService,
    private gtmService: GtmService,
    private urlResolver: CareproviderUnitUrlResolverService,
    private gtmEventPush: GtmGa4EventPushService,
    private bookingHelper: BookingHelperService,
    private appConfigService: AppConfigService,
    private cpruSchemaService: CareproviderUnitSchemaService,
    private reloadService: SearchReloadService,
    private layoutService: LayoutService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    effect(
      () => {
        if (this.careproviderUnit()) {
          this.updateAdsState();
          this.updateMetaTags();
          this.trackPageView();
          this.updateTabs();
          this.isListaDigSuccessful();
          this.cpruSchemaService.removeSchema();
          this.cpruSchemaService.addSchema(this.careproviderUnit());
          if (this.careproviderUnitAddOn()) {
            this.cpruTabsService.handleTabsIndexChange(this.careproviderUnitAddOn() as RoutesAddonsEnums);
          } else {
            this.tools.scrollTo();
          }
          this.isCustomerReviewByKey();
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (
          this.careproviderUnitAddOn() === RoutesAddonsEnums.CAREPROVIDER_CREATE_REVIEW &&
          this.addReviewModalActionStarted() === false
        ) {
          this.addReviewModalActionStarted.set(true);
          this.cpruTabsService.handleTabsIndexChange(RoutesAddonsEnums.CAREPROVIDER_REVIEWS);
          this.openAddReviewModal();
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        this.layoutService.setShouldUseGreyBackground(this.size()[0] >= this.greyBgThreshold);
      },
      {allowSignalWrites: true},
    );
  }

  public ngOnInit() {
    const isRedirected = this.urlResolver.cleanUpUrl(this.route.snapshot);
    this.setListeners();
    this.updateSnapshotDataInServices();
    this.addReviewModalActionStarted.set(false);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.resetNofollow();
    this.cpruSchemaService.removeSchema();
  }

  @HostListener('window:scroll', ['$event'])
  public onScroll() {
    if (this.tabs() && this.tabs().length > 0) {
      const verticalOffset =
        window.pageYOffset || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;

      const coerce = 200;
      let currentSection: CpruTab = null;

      for (let i = this.tabs().length - 1; i >= 0; i--) {
        const elem = this.document.getElementById(`section-${this.tabs()[i].name}`);
        if (elem && verticalOffset + coerce > elem.offsetTop) {
          currentSection = this.tabs()[i];
          break;
        }
      }
      if (currentSection && currentSection.name != this.highlightedSection()) {
        this.cpruTabsService.setHighlightedSection(currentSection.name);
      }
    }
  }

  public handleTabsInViewport({target, visible}: {target: Element; visible: boolean}): void {
    if (visible) {
      this.topSectionCssClass.set(ElementPositionEnum.NORMAL);
      this.bottomSectionCssClass.set('hidden lg:hidden');
      this.placeholderStyle.set({display: 'none'});
    } else {
      const componentElement: HTMLElement = this.document.getElementById('top-section-container') as HTMLElement;
      const height = componentElement.offsetHeight;
      this.topSectionCssClass.set(ElementPositionEnum.FIXED_WITH_SEARCH);
      this.bottomSectionCssClass.set('fixed lg:hidden');
      this.placeholderStyle.set({display: 'block', height: `${height}px`});
    }
  }

  onCloseByDataLoaded($event: boolean) {
    this.closeByDataLoaded.set($event);
  }

  public closeByPuffsChanged($event: boolean) {
    this.closeByHavePuffs.set($event);
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const isRedirected = this.urlResolver.cleanUpUrl(this.route.snapshot);
          this.updateSnapshotDataInServices();
        }
      }),
    );
  }

  private resetNofollow() {
    this.metaService.updateMetaTags([
      {
        value: 'index',
        type: MetaTypesEnum.NAME,
        field: 'robots',
      },
    ]);
  }

  private updateSnapshotDataInServices(): void {
    this.reloadService.updateParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    this.careproviderUnitService.updateCareproviderUnitId(parseInt(this.route.snapshot.params.id, 10));
    this.careproviderUnitService.updateIsPreview(this.route.snapshot.queryParams);
    this.careproviderUnitService.updateCareproviderUnitUrlPath(this.route.snapshot.params.urlPath);
    this.careproviderUnitService.updateCareproviderUnitAddOn(this.route.snapshot.fragment);
  }

  private updateAdsState(): void {
    const dfpState: DfpStateData = this.adsStateService.getDefaultDfpState();
    dfpState.isCustomer =
      this.careproviderUnit() && this.careproviderUnit().isCustomer ? IsCustomerDfpEnum.TRUE : IsCustomerDfpEnum.FALSE;
    if (!this.tools.isNil(this.careproviderUnit().mainCaregiverUnitType)) {
      dfpState.careproviderUnitTypeId = this.careproviderUnit().mainCaregiverUnitType.id;
    }
    dfpState.pageModule = RoutesEnums.CAREPROVIDER;
    if (!this.tools.isNil(this.careproviderUnit().placeId)) {
      dfpState.placeId = this.careproviderUnit().placeId;
    }
    this.adsStateService.updateAdsState(dfpState);
  }

  private updateMetaTags(): void {
    const metaTags: MetaTag[] = [];
    let title = '';
    let description = '';
    let url = '';

    if (!this.tools.isNil(this.careproviderUnit().address)) {
      const city: string = this.careproviderUnit().address.city
        ? this.careproviderUnit().address.city
        : this.careproviderUnit().address.municipality;

      const metaTagsTitle: string = this.careproviderUnit().metaTags.find(
        (tag) => tag.field === MetaTagFieldsEnum.TITLE,
      )?.value;
      const metaTagsDesc: string = this.careproviderUnit().metaTags.find(
        (tag) => tag.field === MetaTagFieldsEnum.DESCRIPTION,
      )?.value;

      title = metaTagsTitle
        ? metaTagsTitle
        : this.titleBuilder.CreateCareproviderTitle(this.careproviderUnit().name, city);
      const address: string = this.tools.isNil(this.careproviderUnit().address.visitingAddress)
        ? `${this.careproviderUnit().address.street} ${this.careproviderUnit().address.zip} ${city}`
        : this.careproviderUnit().address.visitingAddress;
      description = metaTagsDesc
        ? metaTagsDesc
        : `Läs allt om och boka tid hos ${title}. Mottagningen ligger på ${address}.`;
    }
    url = this.urlBuilder.CreateStaticLink(
      this.urlBuilder.CreateCareproviderUnitRouterLink(this.careproviderUnit().id, this.careproviderUnit().urlPath),
    );

    this.pageTitle.set(title);

    metaTags.push({
      value: 'index',
      type: MetaTypesEnum.NAME,
      field: 'robots',
    });

    metaTags.push({
      value: title,
      type: 'title',
      field: 'title',
    });

    metaTags.push({
      value: title,
      type: 'property',
      field: 'og:title',
    });

    metaTags.push({
      value: 'website',
      type: 'property',
      field: 'og:type',
    });

    metaTags.push({
      value: url,
      type: 'property',
      field: 'og:url',
    });

    metaTags.push({
      value: description,
      type: 'name',
      field: 'description',
    });

    metaTags.push({
      value: description,
      type: 'property',
      field: 'og:description',
    });

    metaTags.push({
      value: this.appConfigService.appConfig.siteName,
      type: 'property',
      field: 'og:site_name',
    });

    if (
      !this.tools.isNil(this.careproviderUnit()) &&
      !this.tools.isNil(this.careproviderUnit().caregiverUnitImages) &&
      !this.tools.isNil(this.careproviderUnit().caregiverUnitImages.images) &&
      this.careproviderUnit().caregiverUnitImages.images.length > 0
    ) {
      metaTags.push({
        value: this.tools.getImageUrl(this.careproviderUnit().caregiverUnitImages.images[0].url),
        type: 'property',
        field: 'og:image',
      });

      metaTags.push({
        value: this.tools.getImageUrl(this.careproviderUnit().caregiverUnitImages.images[0].url),
        type: 'name',
        field: 'twitter:image',
      });

      metaTags.push({
        value: 'summary_large_image',
        type: 'name',
        field: 'twitter:card',
      });
    }

    this.metaService.updateMetaTags(metaTags);

    this.linkService.removeTag({rel: 'canonical'});
    this.linkService.updateTag({
      rel: 'canonical',
      href: url,
    });
  }

  private trackPageView(): void {
    if (this.careproviderUnit().timeslotsUpdated === true || this.careproviderUnit().isCustomer === false) {
      let shouldTrack: boolean = this.trackedCareproviderUnitId() !== this.careproviderUnit().id;
      const pageType =
        this.careproviderUnit() && this.careproviderUnit().isCustomer
          ? DLPageTypeEnum.CARE_PROVIDER_PAGE
          : DLPageTypeEnum.HSA_CARE_PROVIDER_PAGE;

      let customParams: DataLayerParams = {
        PageType: pageType,
        IsCustomer: this.careproviderUnit() && this.careproviderUnit().isCustomer,
        Segment: DLSegmentEnum.NOT_SET,
        CareProviderCategory: !this.tools.isNil(this.careproviderUnit().mainCaregiverUnitType)
          ? this.careproviderUnit().mainCaregiverUnitType.name
          : '',
      };
      customParams = this.updateDataLayerConversionOptions(customParams);
      customParams = this.updateDataLayerConversionTracking(customParams);

      const customGa4Params: DataLayerParamsPageViewGA4 = {
        pageType: pageType,
        pageTitle: this.pageTitle(),
        inferredCareProviderTypeAreaId: customParams.InferredCareproviderTypesArea,
        inferredCategoryTypeId: customParams.InferredCareproviderMainTypeID,
        mainCareProviderTypeId: customParams.InferredCareproviderMainTypeID,
        cpruId: customParams.CPRUID,
      };

      let timeslots: number = 0;
      if (!this.tools.isNil(this.careproviderUnit().careServices)) {
        this.careproviderUnit().careServices.forEach((item) => {
          timeslots += item.timeSlots == null ? 0 : item.timeSlots.length;
        });
      }
      customGa4Params.timeSlots = timeslots;
      if (shouldTrack) {
        this.trackedCareproviderUnitId.set(this.careproviderUnit().id);
        this.pageViewGa4Resolver.trackPageView(customGa4Params);
      }
    }
  }

  private updateDataLayerConversionOptions(customParams: DataLayerParams): DataLayerParams {
    customParams.ConversionOptionsBookingEmail = false;
    customParams.ConversionOptionsBookingLinkUntracked = false;
    customParams.ConversionOptionsBookingPhone = false;
    customParams.ConversionOptionsBookingIntegrated = false;
    customParams.ConversionOptionsBookingLinkTracked = false;
    customParams.ConversionOptionsExternalLink = false;
    customParams.ConversionOptionsListing = false;

    if (this.careproviderUnit().bookingSettings && this.careproviderUnit().bookingSettings.length > 0) {
      if (this.careproviderUnit().bookingSettings[0].bookingType.name === BookingTypeEnum.VARDEN) {
        customParams.ConversionOptionsBookingIntegrated = true;
      }

      if (this.careproviderUnit().bookingSettings[0].bookingType.name === BookingTypeEnum.EMAIL) {
        customParams.ConversionOptionsBookingEmail = true;
      }

      if (this.careproviderUnit().bookingSettings[0].bookingType.name === BookingTypeEnum.TEL) {
        customParams.ConversionOptionsBookingPhone = true;
      }

      customParams.ConversionOptionsBookingLinkTracked = this.bookingHelper.isBookingUrlTracked(
        this.careproviderUnit().bookingSettings[0],
      );
      customParams.ConversionOptionsBookingLinkUntracked = !this.bookingHelper.isBookingUrlTracked(
        this.careproviderUnit().bookingSettings[0],
      );
    }

    if (
      this.careproviderUnit().customerFeatures &&
      !this.tools.isNil(
        this.careproviderUnit().customerFeatures.find(
          (feature) => feature.type === CustomerFeatureTypeEnum.LISTA_DIG_DIRECT,
        ),
      )
    ) {
      customParams.ConversionOptionsListing = true;
    }

    if (
      this.careproviderUnit().caregiverContactInfo &&
      this.careproviderUnit().caregiverContactInfo.websiteUrl &&
      this.careproviderUnit().caregiverContactInfo.websiteUrl !== ''
    ) {
      customParams.ConversionOptionsExternalLink = true;
    }

    if (this.careproviderUnit().mainCaregiverUnitType) {
      customParams.InferredCareproviderMainTypeID = this.careproviderUnit().mainCaregiverUnitType.id;

      if (!this.tools.isNil(this.careproviderUnit().mainCaregiverUnitType.area)) {
        customParams.InferredCareproviderTypesArea = this.careproviderUnit().mainCaregiverUnitType.area.id;
      }

      if (!this.tools.isNil(this.careproviderUnit().mainCaregiverUnitType.segment)) {
        customParams.InferredCareproviderTypesSegment = this.careproviderUnit().mainCaregiverUnitType.segment.id;
      }
    }
    customParams.CPRUID = this.careproviderUnit().id;
    return customParams;
  }

  private updateDataLayerConversionTracking(customParams: DataLayerParams): DataLayerParams {
    if (customParams.ConversionOptionsBookingIntegrated === true) {
      customParams.ConversionTracking = DLConversionTrackingEnum.INTERNAL;
    } else if (customParams.ConversionOptionsBookingLinkTracked === true) {
      customParams.ConversionTracking = DLConversionTrackingEnum.EXTERNAL_BOOKING_TRACKED;
    } else if (customParams.ConversionOptionsBookingLinkUntracked === true) {
      customParams.ConversionTracking = DLConversionTrackingEnum.EXTERNAL_BOOKING_UNTRACKED;
    } else {
      customParams.ConversionTracking = DLConversionTrackingEnum.CLICKS_TRACKED;
    }
    return customParams;
  }

  private updateTabs(): void {
    const tabs: CpruTab[] = [];

    const showQuestions =
      (this.careproviderUnit() && this.careproviderUnit().isCustomer && this.showAddQuestionBtn()) ||
      this.visitorQuestions().length > 0;

    const showImpressions = this.careproviderUnit() && this.careproviderUnit().externalReviewSummary !== null;

    if (this.careproviderUnit() && this.careproviderUnit().isCustomer) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_SERVICES,
        label: 'Tjänster',
        mobileOnly: false,
        desktopOnly: false,
      });
    }

    tabs.push({
      name: RoutesAddonsEnums.CAREPROVIDER_ABOUT,
      label: 'Om mottagningen',
      mobileOnly: false,
      desktopOnly: false,
    });

    if (this.survey()) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_SURVEY,
        label: 'Nationella Patientenkäten',
        mobileOnly: false,
        desktopOnly: false,
      });
    }

    if (showQuestions) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_QUESTIONS,
        label: 'Frågor och svar',
        mobileOnly: false,
        desktopOnly: false,
      });
    }

    if (showImpressions) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_IMPRESSION,
        label: 'Helhetsintryck',
        mobileOnly: true,
        desktopOnly: false,
      });
    }

    tabs.push({
      name: RoutesAddonsEnums.CAREPROVIDER_REVIEWS,
      label: 'Omdömen',
      mobileOnly: true,
      desktopOnly: false,
    });

    if (this.hasStaff()) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_STAFF,
        label: 'Personal',
        mobileOnly: false,
        desktopOnly: false,
      });
    }

    if (this.hasReferral()) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_REFERRAL,
        label: 'Remiss',
        mobileOnly: false,
        desktopOnly: false,
      });
    }

    if (this.hasContact()) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_CONTACT,
        label: 'Kontakt',
        mobileOnly: false,
        desktopOnly: false,
      });
    }

    if (this.hasOpenHours()) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_OPENHOURS,
        label: 'Öppettider',
        mobileOnly: false,
        desktopOnly: false,
      });
    }

    if (this.hasMap()) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_MAP,
        label: 'Hitta till mottagningen',
        mobileOnly: true,
        desktopOnly: false,
      });
    }

    this.cpruTabsService.updateTabs(tabs);
  }

  private isListaDigSuccessful() {
    const listaDigSuccess = this.route.snapshot.queryParamMap.get(this.listadigSuccessQueryParamName);

    if (listaDigSuccess === null) return;

    let title = 'Bekräftelse';
    let message =
      'Vi har skickat vidare uppgifterna för listning till ' +
      this.careproviderUnit().name +
      '. Det kan ta några dagar innan listningen registreras hos vårdgivaren,' +
      ' men du kan redan nu boka tid hos ' +
      this.careproviderUnit().name +
      '. Tack för att du har använt <a href="https://www.varden.se">Vården.se!</a>';

    if (listaDigSuccess !== 'True') {
      title = 'Felmeddelande';
      message = 'Något gick fel.';
    }

    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.INFO,
      data: {title, message},
    };
    this.modalControl.openModal(modalCommand);

    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.LISTA_DIG_VARDEN,
      Action: EventActionEnum.LISTA_DIG_VARDEN_BANKIDSUCCESS,
      Label: this.featureLabel(),
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.LISTA_DIG_VARDEN} - ${EventActionEnum.LISTA_DIG_VARDEN_BANKIDSUCCESS}`,
      cpruId: this.careproviderUnit().id,
      pageType: DLPageTypeEnum.CARE_PROVIDER_PAGE,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);
  }

  private openAddReviewModal(): void {
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.RATING,
      data: this.careproviderUnit(),
    };
    this.modalControl.openModal(modalCommand);
  }

  private openCustomerRatingModal(reviewRatingKey: string) {
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.CUSTOMER_RATING,
      data: [this.careproviderUnit().name, reviewRatingKey],
    };
    this.modalControl.openModal(modalCommand);
  }

  private isCustomerReviewByKey(): void {
    const params = this.route.snapshot.queryParams;

    if (
      !this.tools.isNil(params) &&
      !this.tools.isNil(params[RoutesQueryParamsEnum.REVIEW_RATING_KEY]) &&
      params[RoutesQueryParamsEnum.REVIEW_RATING_KEY].length > 0
    ) {
      this.cpruTabsService.handleTabsIndexChange(RoutesAddonsEnums.CAREPROVIDER_REVIEWS);
      this.openCustomerRatingModal(params[RoutesQueryParamsEnum.REVIEW_RATING_KEY]);
    }
  }
}
